// src/components/layout/Header.tsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { useBrandTheme } from "../../contexts/BrandThemeContext";
import { DEFAULT_PLATFORM_LOGO_URL } from "../../modules/platform/api/branding.types";
import { useTheme } from "../../contexts/ThemeContext";
import { Moon, Sun, LogOut } from "lucide-react";
import { UserAvatar } from "../ui/UserAvatar";
import { SupportNotificationBell } from "../../modules/support/components/SupportNotificationBell";
import { ROUTES } from "../../utils/constants";
import { getLoginPathForCurrentSession } from "../../utils/loginPortal";
import { useUserAvatarInfo } from "../../hooks/useUserAvatarInfo";
import { HeaderLanguageSelector } from "./HeaderLanguageSelector";
import { useSchoolLanguageOptional } from "../../contexts/SchoolLanguageContext";
import { resolveLogoutDestinationPath } from "../../utils/publicTenantHandoff";

interface HeaderProps {
  onMenuClick?: () => void;
}

const ROLE_LABELS: Record<string, string> = {
  DEV_TEAM: "Dev Team",
  SCHOOL_ADMIN: "School Admin",
  TEACHER: "Teacher",
  STUDENT: "Student",
  PARENT: "Parent",
};

const NAV_PATTERN = "/images/nav-tamil-pattern.svg";

function getRoleLabel(role: string | undefined, t: (key: string, opts?: { defaultValue?: string }) => string): string {
  if (!role) return t("header.user", { defaultValue: "User" });
  const key = `header.roles.${role}`;
  return t(key, { defaultValue: ROLE_LABELS[role] ?? role.replace(/_/g, " ") });
}

export const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { avatarUrl, initials, displayName } = useUserAvatarInfo();
  const {
    logoUrl,
    displayName: brandDisplayName,
    displayNameTa,
    loading: brandingLoading,
    brandingReady,
    tenantVisualsExpected,
  } = useBrandTheme();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [logoSrc, setLogoSrc] = useState<string | null>(null);
  const [logoReady, setLogoReady] = useState(false);

  const showBrandingSkeleton = tenantVisualsExpected && (!brandingReady || brandingLoading);

  useEffect(() => {
    if (showBrandingSkeleton) {
      setLogoSrc(null);
      setLogoReady(false);
      return;
    }
    const nextLogo = logoUrl?.trim() || DEFAULT_PLATFORM_LOGO_URL;
    if (!nextLogo) {
      setLogoSrc(null);
      setLogoReady(false);
      return;
    }
    let cancelled = false;
    setLogoReady(false);
    const img = new Image();
    img.onload = () => {
      if (!cancelled) {
        setLogoSrc(nextLogo);
        setLogoReady(true);
      }
    };
    img.onerror = () => {
      if (!cancelled) {
        setLogoSrc(tenantVisualsExpected ? null : DEFAULT_PLATFORM_LOGO_URL);
        setLogoReady(true);
      }
    };
    img.src = nextLogo;
    return () => {
      cancelled = true;
    };
  }, [logoUrl, showBrandingSkeleton, tenantVisualsExpected]);

  const handleLogoError = () => {
    if (tenantVisualsExpected) {
      setLogoSrc(null);
      return;
    }
    if (logoSrc !== DEFAULT_PLATFORM_LOGO_URL) {
      setLogoSrc(DEFAULT_PLATFORM_LOGO_URL);
    }
  };

  const schoolLangCtx = useSchoolLanguageOptional();

  const handleLogout = async () => {
    // Resolve public landing destination BEFORE AuthContext clears tenant intent/session
    const logoutDestination = resolveLogoutDestinationPath({
      schoolLanguage: schoolLangCtx?.schoolDefault
        ? { ui_default: schoolLangCtx.schoolDefault, allowed: schoolLangCtx.allowedLanguages }
        : null,
      uiLanguage: schoolLangCtx?.language,
    });

    try {
      await logout();
    } finally {
      navigate(logoutDestination, { replace: true });
    }
  };

  const headerUserLabel = displayName ?? getRoleLabel(user?.roles[0], t);

  const getDashboardRoute = () => {
    if (!user) return ROUTES.LOGIN;
    if (user.roles.includes("DEV_TEAM")) return ROUTES.DEV_DASHBOARD;
    if (user.roles.includes("SCHOOL_ADMIN")) return ROUTES.ADMIN_DASHBOARD;
    if (user.roles.includes("TEACHER")) return ROUTES.TEACHER_DASHBOARD;
    if (user.roles.includes("STUDENT")) return ROUTES.STUDENT_DASHBOARD;
    if (user.roles.includes("PARENT")) return ROUTES.PARENT_DASHBOARD;
    return ROUTES.LOGIN;
  };

  return (
    <header className="sticky top-0 z-50 h-[4.25rem] sm:h-[4.5rem] shrink-0 overflow-hidden shadow-md shadow-app-primary/15">
      <div
        className="absolute inset-0 bg-gradient-to-r from-app-primary via-app-primary/95 to-app-primary/80"
        aria-hidden
      />
      <div
        className="absolute inset-0 opacity-90 bg-repeat"
        style={{ backgroundImage: `url(${NAV_PATTERN})`, backgroundSize: "320px 72px" }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/5" aria-hidden />

      <div className="relative px-2 sm:px-5 lg:px-8 h-full">
        <div className="flex flex-nowrap items-center justify-between h-full min-w-0 gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 overflow-hidden flex-1">
            {onMenuClick && (
              <button
                onClick={onMenuClick}
                aria-label={t("common.toggleSidebar", { defaultValue: "Toggle sidebar" })}
                className="flex flex-col items-center justify-center gap-1.5 rounded-xl p-2 sm:p-2.5 transition-colors hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 shrink-0"
              >
                <span className="h-0.5 w-6 sm:w-7 rounded-full bg-white/95" />
                <span className="h-0.5 w-6 sm:w-7 rounded-full bg-white/95" />
                <span className="h-0.5 w-6 sm:w-7 rounded-full bg-white/95" />
              </button>
            )}

            <Link
              to={getDashboardRoute()}
              className="flex items-center gap-2 sm:gap-3 min-w-0"
              title={
                showBrandingSkeleton
                  ? undefined
                  : brandDisplayName || t("header.brandNameEn", { defaultValue: "Tamil Katral Maiyam" })
              }
            >
              <span className="h-9 w-9 sm:h-10 sm:w-10 shrink-0 rounded-full bg-white/95 p-0.5 shadow-sm ring-2 ring-white/40 overflow-hidden">
                {showBrandingSkeleton || !logoReady || !logoSrc ? (
                  <span className="block h-full w-full animate-pulse bg-white/60" aria-hidden />
                ) : (
                  <img
                    src={logoSrc}
                    alt=""
                    className="h-full w-full object-contain"
                    draggable={false}
                    onError={handleLogoError}
                  />
                )}
              </span>
              <span className="min-w-0 leading-tight">
                <span className="block text-sm sm:text-base font-bold text-white tamil-text truncate drop-shadow-sm">
                  {showBrandingSkeleton ? (
                    <span className="inline-block h-4 w-28 animate-pulse rounded bg-white/40 align-middle" aria-hidden />
                  ) : (
                    displayNameTa || t("header.brandNameTa", { defaultValue: "தமிழ் கற்றல் மையம்" })
                  )}
                </span>
                <span className="hidden sm:block text-[10px] sm:text-xs font-semibold text-white/90 truncate">
                  {showBrandingSkeleton ? (
                    <span className="inline-block h-3 w-24 animate-pulse rounded bg-white/40 align-middle" aria-hidden />
                  ) : (
                    brandDisplayName || t("header.brandNameEn", { defaultValue: "Tamil Katral Maiyam" })
                  )}
                </span>
              </span>
            </Link>
          </div>

          <div className="flex flex-nowrap items-center gap-1.5 sm:gap-2.5 shrink-0">
            {user ? <SupportNotificationBell /> : null}
            {user ? <HeaderLanguageSelector /> : null}
            <button
              onClick={toggleTheme}
              className="p-2 sm:p-2.5 rounded-full bg-white/15 hover:bg-white/25 text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              aria-label={t("common.toggleTheme", { defaultValue: "Toggle theme" })}
            >
              {theme === "dark" ? (
                <Sun className="w-5 h-5 text-amber-200" />
              ) : (
                <Moon className="w-5 h-5 text-white" />
              )}
            </button>

            {user && (
              <>
                <Link
                  to="/profile"
                  className="relative inline-flex shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                  aria-label={t("common.profile", { defaultValue: "Profile" })}
                >
                  <UserAvatar
                    avatarUrl={avatarUrl}
                    initials={initials}
                    size="header"
                    showOnlineStatus
                  />
                </Link>

                <span
                  className="hidden sm:inline text-xs font-bold text-app-primary bg-white/90 px-2.5 py-1 rounded-full shadow-sm truncate max-w-[7rem]"
                  title={headerUserLabel}
                >
                  {headerUserLabel}
                </span>

                <div className="p-[2px] rounded-2xl bg-gradient-to-r from-app-primary/50 via-app-primary/35 to-app-primary/25 shrink-0">
                  <button
                    type="button"
                    onClick={() => void handleLogout()}
                    data-testid="header-sign-out-button"
                    className="flex items-center justify-center gap-1.5 sm:gap-2 h-9 sm:h-10 px-2.5 sm:px-4 rounded-[0.875rem] bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white font-bold text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 min-w-[2.25rem] sm:min-w-0"
                    aria-label={t("common.signOut", { defaultValue: "Sign out" })}
                  >
                    <LogOut className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                    <span className="hidden sm:inline">{t("common.signOut", { defaultValue: "Sign out" })}</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
